import axios from "axios";
import moment from "moment-timezone";
import { useState } from "react";
import { NotificationManager } from "react-notifications";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../../routes/routeKeys";
import ActionsMenu from "../../../components/ActionsMenu";
import DeleteConfirmationModal from "../../../components/ConfirmationModals/DeleteConfirmationModal";
import BrokerDetailsModal from "./Modals/BrokerDetailsModal";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import { formatPhoneNumber } from "../../../utils/utils";

const PropertyDetailHeader = ({ propertydata }) => {
  const { id } = useParams();
  const [config] = useAuth();
  const navigate = useNavigate();

  const [buyersExpanded, setBuyersExpanded] = useState(false);
  const [sellersExpanded, setSellersExpanded] = useState(false);

  const [disableDelete, setDisableDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState(null);

  const brokers = [];
  if (propertydata?.buyer_broker) {
    Object.entries(propertydata.buyer_broker).forEach(([role, details]) => {
      if (details) {
        if (Array.isArray(details)) {
          details.forEach(d => brokers.push({ role, details: d }));
        } else {
          brokers.push({ role, details });
        }
      }
    });
  }
  
  if (propertydata?.seller_broker) {
    Object.entries(propertydata.seller_broker).forEach(([role, details]) => {
      if (details) {
        if (Array.isArray(details)) {
          details.forEach(d => brokers.push({ role, details: d }));
        } else {
          brokers.push({ role, details });
        }
      }
    });
  }

  const buyerBrokers = brokers.filter(b => b.role.startsWith('buyer'));
  const sellerBrokers = brokers.filter(b => b.role.startsWith('seller'));
  const handleDelete = () => {
    setDisableDelete(true);

    axios
      .get(`${BASE_URL}/delete-property/${id}`, config)
      .then((res) => {
        if (res.data.success) {
          navigate(ROUTES.PROPERTIES);
        }
        setShowDeleteModal(false);

        if (res?.data?.message) {
          NotificationManager.success(res.data.message);
        }
      })
      .catch((err) => {
        if (
          err.response?.data?.message &&
          err.response?.data?.message !== "Too Many Attempts."
        ) {
          NotificationManager.error(err.response.data.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const handleProfileNavigate = (broker) => {
    const { role, details } = broker;
    const isInternalUser = role.includes("inside") || role.includes("bds") || details?.round_robin !== undefined;
    
    if (isInternalUser) {
      navigate(ROUTES.USER_DETAILS.replace(":id", details?.id));
    } else {
      navigate(ROUTES.CONTACT_DETAILS.replace(":id", details?.id));
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-3">

      {/* Header */}
      <div className="flex items-start justify-between border-b pb-2">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {propertydata?.property_name}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {propertydata?.property_type?.type}
          </p>
          
        </div>

        <ActionsMenu
          handleEdit={() => navigate(ROUTES.EDIT_PROPERTY.replace(":id", id))}
          handleDelete={() => setShowDeleteModal(true)}
        />
      </div>

      {brokers.length > 0 && (
        <div className={`grid mt-4 w-full border-b pb-4 border-gray-100 ${buyersExpanded || sellersExpanded ? 'grid-cols-1 gap-8' : 'grid-cols-1 md:grid-cols-2'}`}>
          {buyerBrokers.length > 0 && (
            <div className={`w-full ${!(buyersExpanded || sellersExpanded) && sellerBrokers.length > 0 ? 'md:pr-12 md:border-r border-gray-200' : ''}`}>
              <div 
                className="flex items-center justify-between py-2 cursor-pointer hover:bg-gray-50 transition-colors rounded"
                onClick={() => setBuyersExpanded(!buyersExpanded)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-700">
                    <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="20" width="20" xmlns="http://www.w3.org/2000/svg"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-[15px]">Buyer </p>
                    <p className="text-xs text-gray-500 mt-0.5">People representing the buyer</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-green-700">{buyerBrokers.length} broker{buyerBrokers.length !== 1 ? 's' : ''}</span>
                  <span className="text-gray-400 text-xs">
                    {buyersExpanded ? "▲" : "▼"}
                  </span>
                </div>
              </div>
              
              {buyersExpanded && (
                <div className="pt-4 flex flex-wrap gap-4">
                  {buyerBrokers.map((broker, idx) => (
                    <div key={idx} className="flex items-center justify-between border border-gray-200 rounded-lg p-3 bg-white w-full md:w-[320px] border-l-4 border-l-green-600 shadow-sm">
                      <div className="flex items-center gap-3">
                         <div className="w-10 h-10 rounded-full bg-green-700 text-white flex items-center justify-center text-sm font-medium shrink-0">
                           {`${broker.details?.first_name?.charAt(0) || ""}${broker.details?.last_name?.charAt(0) || ""}`.toUpperCase()}
                         </div>
                         <div>
                           <p 
                             role="button"
                             onClick={(e) => { e.stopPropagation(); handleProfileNavigate(broker); }}
                             className="font-semibold text-gray-900 text-sm mb-1 line-clamp-1 hover:underline cursor-pointer"
                           >
                             {broker.details?.name || `${broker.details?.first_name || ""} ${broker.details?.last_name || ""}`.trim()}
                           </p>
                           <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs font-medium capitalize border border-green-100">{broker.role.replace(/_/g, " ")}</span>
                         </div>
                      </div>
                      <button onClick={() => setSelectedBroker(broker)} className="text-green-700 hover:text-green-800 text-xs font-medium flex items-center gap-1 whitespace-nowrap ml-2">
                        View details →
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {sellerBrokers.length > 0 && (
            <div className={`w-full ${!(buyersExpanded || sellersExpanded) && buyerBrokers.length > 0 ? 'md:pl-12' : ''}`}>
              <div 
                className="flex items-center justify-between py-2 cursor-pointer hover:bg-gray-50 transition-colors rounded"
                onClick={() => setSellersExpanded(!sellersExpanded)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-700">
                    <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="20" width="20" xmlns="http://www.w3.org/2000/svg"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-[15px]">Seller </p>
                    <p className="text-xs text-gray-500 mt-0.5">People representing the seller</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-green-700">{sellerBrokers.length} broker{sellerBrokers.length !== 1 ? 's' : ''}</span>
                  <span className="text-gray-400 text-xs">
                    {sellersExpanded ? "▲" : "▼"}
                  </span>
                </div>
              </div>
              
              {sellersExpanded && (
                <div className="pt-4 flex flex-wrap gap-4">
                  {sellerBrokers.map((broker, idx) => (
                    <div key={idx} className="flex items-center justify-between border border-gray-200 rounded-lg p-3 bg-white w-full md:w-[320px] border-l-4 border-l-green-600 shadow-sm">
                      <div className="flex items-center gap-3">
                         <div className="w-10 h-10 rounded-full bg-green-700 text-white flex items-center justify-center text-sm font-medium shrink-0">
                           {`${broker.details?.first_name?.charAt(0) || ""}${broker.details?.last_name?.charAt(0) || ""}`.toUpperCase()}
                         </div>
                         <div>
                           <p 
                             role="button"
                             onClick={(e) => { e.stopPropagation(); handleProfileNavigate(broker); }}
                             className="font-semibold text-gray-900 text-sm mb-1 line-clamp-1 hover:underline cursor-pointer"
                           >
                             {broker.details?.name || `${broker.details?.first_name || ""} ${broker.details?.last_name || ""}`.trim()}
                           </p>
                           <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs font-medium capitalize border border-green-100">{broker.role.replace(/_/g, " ")}</span>
                         </div>
                      </div>
                      <button onClick={() => setSelectedBroker(broker)} className="text-green-700 hover:text-green-800 text-xs font-medium flex items-center gap-1 whitespace-nowrap ml-2">
                        View details →
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Top Grid */}
      <div className="grid grid-cols-5 gap-6 mt-3 items-start">

        {/* Last Updated */}
        <div>
          <p className="text-xs text-gray-500">Last Update</p>
          <p className="body-N  font-medium text-gray-900">
            {moment(propertydata?.updated_at)
              .tz("America/New_York")
              .format("MM/DD/YYYY")}
          </p>
        </div>

        {/* Store */}
        <div>
          <p className="text-xs text-gray-500">Store #</p>
          <p className="text-sm font-medium text-gray-900">
            {propertydata?.store || "-"}
          </p>
        </div>

        {/* Address */}
        <div>
          <p className="text-xs text-gray-500">Address</p>
          <p className="body-N  font-medium text-gray-900">
            {propertydata?.street_address
              ? `${propertydata.street_address}, ${propertydata.city}, ${propertydata.state}, ${propertydata.zipcode}`
              : "-"}
          </p>
        </div>

        {/* Links */}
        <div>
          <p className="text-xs text-gray-500">Links</p>

          <div className="flex flex-col gap-2 mt-1">
            {propertydata?.google_map_link && (
              <button
                onClick={() =>
                  window.open(propertydata.google_map_link, "_blank")
                }
                className="green-H text-sm underline  text-left"
              >
                View on Google Maps
              </button>
            )}

            {propertydata?.rpr_link && (
              <button
                onClick={() => window.open(propertydata.rpr_link, "_blank")}
                className="border px-2 py-1 rounded-md text-xs w-fit hover:bg-gray-100"
              >
                RPR Link
              </button>
            )}
          </div>
        </div>

        {/* Owner */}
        <div>
          <p className="text-xs text-gray-500">Owner</p>

          <div className="">
            <p className="head-5 green-H underline">
              <Link
                href={
                  propertydata?.owner_company_id
                    ? `/company/${propertydata?.owner_company_id}`
                    : `/contact/${propertydata?.owner_contact_id}`
                }
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  if (propertydata?.owner_company_id) {
                    navigate(ROUTES.COMPANY_DETAILS.replace(":id", propertydata?.owner_company_id));
                  } else {
                    navigate(ROUTES.CONTACT_DETAILS.replace(":id", propertydata?.owner_contact_id));
                  }
                }}
                className="underline"
              >
                {propertydata?.owner?.name}
              </Link>
            </p>

            {propertydata?.owner_phone?.[0]?.phone_number && (
              <span className=" body-N text-gray-600 truncate">
                {formatPhoneNumber(propertydata.owner_phone[0].phone_number)}
              </span>
            )}

            {propertydata?.owner_phone?.[0]?.phone_category && (
              <span className="body-N  green-H capitalize">
                {propertydata.owner_phone[0].phone_category.replace(/_/g, " ")}
              </span>
            )}
          </div>
        </div>

      </div>

      {/* Bottom Section */}
      <div className="grid md:grid-cols-3 gap-6 mt-8 border-t pt-6">

        {/* Comments */}
        <div>
          <p className="text-xs text-gray-500">Comments</p>
          <p className="text-sm text-gray-800 mt-1">
            {propertydata?.comment || "No comments"}
          </p>
        </div>

        {/* Property Type */}
        <div>
          <p className="text-xs text-gray-500">Property Type</p>
          <span className="inline-block mt-2 px-3 py-1 text-xs  text-green-700 rounded-full">
            {propertydata?.property_type?.type}
          </span>
        </div>

        {/* Tags */}
        <div>
          <p className="text-xs text-gray-500">Tags</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {propertydata?.propertytag_details?.map((el, i) => (
              <span
                key={i}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
              >
                {el?.tag_name}
              </span>
            ))}
          </div>
        </div>

      </div>

      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />
      
      <BrokerDetailsModal
        showModal={!!selectedBroker}
        onClose={() => setSelectedBroker(null)}
        broker={selectedBroker}
      />
    </div>
  );
};

export default PropertyDetailHeader;